import { useCallback, useEffect, useRef, useState } from "react";

export type CopyState = "idle" | "copied" | "error";

interface Options {
  resetDelay?: number;
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

interface Return {
  copy: (value: string) => void;
  state: CopyState;
  isIdle: boolean;
  isCopied: boolean;
  isError: boolean;
}

async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    if (ok) {
      resolve();
    } else {
      reject(new Error("document.execCommand('copy') returned false"));
    }
  });
}

export function useCopyToClipboard({
  resetDelay = 2000,
  onSuccess,
  onError,
}: Options = {}): Return {
  const [state, setState] = useState<CopyState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const copy = useCallback(
    (value: string) => {
      if (state !== "idle") return;
      writeToClipboard(value)
        .then(() => {
          setState("copied");
          onSuccess?.();
          timerRef.current = setTimeout(() => setState("idle"), resetDelay);
        })
        .catch((err: unknown) => {
          setState("error");
          onError?.(err);
          timerRef.current = setTimeout(() => setState("idle"), resetDelay);
        });
    },
    [state, resetDelay, onSuccess, onError],
  );

  return { copy, state, isIdle: state === "idle", isCopied: state === "copied", isError: state === "error" };
}
